import React, { useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@material-ui/core';
import { Search } from '@material-ui/icons';

import './userBadge.css';

import { modalPages as mp } from 'constants/modalPage';
import { getConnectionHealth } from 'reducers/connectivity/connection';
import { useBreakpoints } from 'utils/hooks/UI';
import { openModalPage } from 'actions/modalPage';
import UIButton from 'components/UIElements/UIButton';
import { getUISetting } from 'reducers/configs/settings';

type Props = {
  /** Name of the customer - displayed in bold in the top row */
  customerName: string;
  /** The card number for this customer - displayed just below the customer name */
  customerCardNumber: string;
  /** The group of the customer - displayed below the customerCardNumber */
  customerGroup: string;
  /** Determines the default icon that will be displayed in the user badge */
  isCustomer: boolean;
  /** Determines the icon and functionality for user badge */
  isDefaultCustomer: boolean;
  /** Shows the latest clock in time if any for a customer */
  loggedInEmployeeLastClockInTime?: string | null;
  /** Check if fetching employeee stats failed */
  isEmployeeError: boolean;
  /** Retry function to fetch employee data and other info */
  onRetryEmployeeData: () => void;
  /** Check if employee stats are being fetched */
  isLoadingEmployee: boolean;
};

/** Component that displays an icon an specific data next to it */
const UserBadge: React.FC<Props> = ({
  customerName,
  customerCardNumber,
  customerGroup,
  isCustomer = false,
  isDefaultCustomer,
  loggedInEmployeeLastClockInTime,
  isEmployeeError,
  onRetryEmployeeData,
  isLoadingEmployee,
}) => {
  const dispatch = useDispatch();

  const searchCustomer = useCallback(
    () => dispatch(openModalPage({ component: mp.searchCustomers })),
    [dispatch],
  );

  const prefix = !isCustomer ? 'employee' : 'customer';

  const mobileView = !useBreakpoints().mdplus;
  const connectionHealth = useSelector(getConnectionHealth);
  const hideCustomerInfo = useSelector(getUISetting('hideCustomerInfo'));
  const hideCustomerSearch = useSelector(getUISetting('hideCustomerSearch'));

  const showUserPage = () => {
    if (!isCustomer || !connectionHealth) return;
    const view = isDefaultCustomer ? mp.createCustomer : mp.customerView;
    dispatch(
      openModalPage({
        component: view,
        // @ts-ignore
        groupID: view,
        replace: true,
        props: {
          newCustomer: isDefaultCustomer,
        },
      }),
    );
  };

  useTranslation('customer'); // ensure customer namespace also loaded
  const { t } = useTranslation('employee');

  return (
    <Container
      className={`${mobileView ? 'p-0' : 'user-badge'}`}
      style={{ cursor: isCustomer ? 'pointer' : 'initial' }}
    >
      <Row
        className="user-badge-icon-wrapper"
        data-testid={`${prefix}-badge-container`}
      >
        {!mobileView && (
          <Col xs="auto" className="user-icon" onClick={showUserPage}>
            {isCustomer ? (
              <>
                <div className="user-badge-icon">
                  <span
                    className={`user-badge-icon-span ${
                      isDefaultCustomer ? 'icon_plus_alt' : 'icon_profile'
                    }`}
                    data-testid={`${prefix}-badge-icon-span`}
                    style={{
                      cursor: !connectionHealth ? 'not-allowed' : '',
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="user-badge-icon">
                <span className="user-badge-icon-span icon_profile" />
              </div>
            )}
          </Col>
        )}

        {!mobileView && (
          <Col
            className="user-badge-data-wrapper"
            data-testid={`"display-${prefix}-view"`}
          >
            <Row className="user-badge-name" data-testid={`${prefix}-name`}>
              {customerName || t('customer:noSelectedCustomer')}
            </Row>
            {!isCustomer && loggedInEmployeeLastClockInTime ? (
              <Row>
                <span
                  data-testid="employee-clock-in-time"
                  className="employee-clock-in-time"
                >
                  {t('clockInTime', {
                    time: loggedInEmployeeLastClockInTime,
                  })}
                </span>
              </Row>
            ) : null}
            {!isDefaultCustomer && isCustomer && (
              <>
                <Row>
                  <Col data-testid={`${prefix}-card-number`}>
                    {customerCardNumber}
                  </Col>
                </Row>
                <Row>
                  <Col
                    style={{
                      fontWeight: 700,
                      marginTop: '2px',
                      fontSize: '16px',
                    }}
                    data-testid={`${prefix}-group`}
                  >
                    {customerGroup}
                  </Col>
                </Row>
              </>
            )}
          </Col>
        )}
        {isEmployeeError && (
          <UIButton
            text={t('retry')}
            block={false}
            className="client-details-button"
            action={onRetryEmployeeData}
            disabled={isLoadingEmployee}
          />
        )}
      </Row>
      {mobileView && (!hideCustomerInfo || !hideCustomerSearch) ? (
        <Row>
          <Col className="user-badge-data-wrapper pl-0">
            <Row
              className="user-badge-name"
              style={{
                alignItems: 'center',
                justifyContent: hideCustomerInfo ? 'end' : 'start',
              }}
            >
              {!hideCustomerInfo ? (
                <>
                  <div
                    className="user-badge-icon"
                    style={{
                      height: '36px',
                      width: '36px',
                      borderRadius: 0,
                      marginRight: '8px',
                    }}
                    onClick={showUserPage}
                  >
                    <span
                      className={`user-badge-icon-span ${
                        isDefaultCustomer ? 'icon_plus_alt' : 'icon_profile'
                      }`}
                      data-testid={`${prefix}-badge-icon-span`}
                      style={{
                        fontSize: '36px',
                        lineHeight: 1,
                        cursor: !connectionHealth ? 'not-allowed' : '',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      flexGrow: 1,
                      textOverflow: 'ellipsis',
                      flexBasis: 0,
                      marginRight: '8px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      fontSize: '18px',
                    }}
                  >
                    {customerName || t('customer:noSelectedCustomer')}
                  </span>
                </>
              ) : null}
              {!hideCustomerSearch ? (
                <IconButton
                  onClick={searchCustomer}
                  size="small"
                  style={{ border: '2px solid' }}
                >
                  <Search style={{ fontSize: '26px' }} />
                </IconButton>
              ) : null}
            </Row>
            {!isCustomer && loggedInEmployeeLastClockInTime ? (
              <Row>
                <span className="employee-clock-in-time">
                  {t('clockInTime', {
                    time: loggedInEmployeeLastClockInTime,
                  })}
                </span>
              </Row>
            ) : null}
          </Col>
        </Row>
      ) : null}
    </Container>
  );
};

export default UserBadge;
